import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Badge, Spinner, Pagination, Alert, Collapse } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  PvPRoom,
  PvPRoomFilters,
  PvpRoomInvitePreview,
  Quiz,
  HostedOpenAsyncPvPSummary,
  authService,
  inventoryService,
  pvpService,
  quizService,
} from '../services/api';
import { shouldPromptXpPotionReminder } from '../utils/inventoryXpPotions';
import {
  inventorySlotsHaveUnusedHealthPotion,
  shouldPromptHealthPotionReminder,
} from '../utils/inventoryHealthPotions';
import XpPotionReminderModal from './XpPotionReminderModal';
import HealthPotionReminderModal from './HealthPotionReminderModal';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { useMatchMedia } from '../hooks/useMatchMedia';
import { InlineHalfHeartIcon, isUnlimitedUserType } from './LivesDisplay';
import LivesGateModal from './LivesGateModal';
import { isInsufficientLivesMessage } from '../utils/livesErrorI18n';
import LoginPage from './LoginPage';
import './QuizList.css';
import './PvPLobby.css';
import { getQuizterSiteUrl, sharePvpInviteLink } from '../utils/shareAchievementImage';
import {
  getQuizDisplayTitle,
  getQuizDisplayTheme,
  getQuizFullDisplayCaption,
} from '../utils/quizDisplayTitle';
import PvPRoomLobbyExpiryCountdown from './PvPRoomLobbyExpiryCountdown';
import QuizLockedModal from './QuizLockedModal';
import { isQuizLockedApiError } from '../utils/quizLockedApiError';
import { isQuizCareerLocked } from '../utils/quizCareerLock';
import { MOBILE_QUIZ_CARD_COLS_STORAGE_KEY } from '../constants/mobileQuizCardLayoutStorage';
import {
  SOLO_PVP_LOBBY_DEFAULT_PAGE_SIZE,
  SOLO_PVP_LOBBY_PAGE_SIZE_OPTIONS,
} from '../constants/soloPvpLobbyPagination';
import { careerHomePathWithSearch } from '../constants/careerSearchParams';
import {
  defaultHighestUnlockedPlayDifficulty,
  isPlayBandCareerLocked,
  playModeChoices,
} from '../utils/quizPlayModes';
import { isPlayDifficultyLockedApiError } from '../utils/quizLockedApiError';

function isStopQuizFormat(quiz: Quiz): boolean {
  const f = (quiz.format || '').toString();
  return f === 'Stop' || f === 'stop';
}

function isGeniusQuizFormat(quiz: Quiz): boolean {
  const f = (quiz.format || '').toString();
  return f === 'Genius' || f === 'genius';
}

function isWordConnectQuizFormat(quiz: Quiz): boolean {
  const f = (quiz.format || '').toString();
  return f === 'WordConnect' || f === 'wordconnect';
}

interface CreateRoomData {
  name: string;
  quizId: string;
  playDifficulty: string;
}

interface PvPLobbyProps {
  isUserLoggedIn?: boolean;
  onLoginSuccess?: () => void;
  /** From <c>/?tab=pvp&amp;pvpJoin=CODE</c> (invite link). */
  pvpJoinCode?: string | null;
  onPvpJoinConsumed?: () => void;
}

const PvPLobby: React.FC<PvPLobbyProps> = ({
  isUserLoggedIn = true,
  onLoginSuccess,
  pvpJoinCode = null,
  onPvpJoinConsumed,
}) => {
  const { t, i18n } = useSafeTranslation();
  /** Countdown do lobby async: no mobile fica na meta (abaixo do host); em md+ ao lado do botão. */
  const isMdUp = useMatchMedia('(min-width: 768px)');
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<PvPRoom[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [joining, setJoining] = useState(false);
  const [createModalQuizzes, setCreateModalQuizzes] = useState<Quiz[]>([]);
  const [createModalQuizzesLoading, setCreateModalQuizzesLoading] = useState(false);
  const [createForm, setCreateForm] = useState<CreateRoomData>({
    name: '',
    quizId: '',
    playDifficulty: '',
  });
  const [myUserId, setMyUserId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /** Synchronous guard — blocks a second create before React re-renders with creating=true. */
  const createRoomInFlightRef = useRef(false);
  const [activePvpRoom, setActivePvpRoom] = useState<PvPRoom | null>(null);
  /** Salas async abertas como host (limite 3 + quiz distinto) — espelha API. */
  const [hostedOpenSummary, setHostedOpenSummary] = useState<HostedOpenAsyncPvPSummary | null>(null);
  const [livesGateOpen, setLivesGateOpen] = useState(false);
  const [livesGateMessage, setLivesGateMessage] = useState('');
  /** True while checking lives before opening the create-room modal */
  const [createLivesCheckLoading, setCreateLivesCheckLoading] = useState(false);
  /** Unused XP potions in inventory — remind before create-room modal */
  const [xpPotionPvpGateOpen, setXpPotionPvpGateOpen] = useState(false);
  /** Health potion reminder (≤5 lives, not full) — before create-room (after XP gate if any) */
  const [healthPotionGateOpen, setHealthPotionGateOpen] = useState(false);
  const [healthReminderPendingPvpCreate, setHealthReminderPendingPvpCreate] = useState(false);
  /** Invite link / lobby card: mesmo modal; <c>source</c> distingue URL <c>?pvpJoin=</c> do clique na Arena. */
  const [inviteJoinModal, setInviteJoinModal] = useState<{
    open: boolean;
    loading: boolean;
    error: string | null;
    preview: PvpRoomInvitePreview | null;
    source: 'url' | 'lobby' | null;
  }>({ open: false, loading: false, error: null, preview: null, source: null });

  /** Título do quiz bloqueado (desbloqueável) ao tentar criar sala PvP. */
  const [pvpQuizLockedTitle, setPvpQuizLockedTitle] = useState<string | null>(null);

  const loadActivePvpRoom = useCallback(async () => {
    if (!isUserLoggedIn) {
      setActivePvpRoom(null);
      return;
    }
    try {
      const room = await pvpService.getMyActivePvpRoom();
      setActivePvpRoom(room);
    } catch {
      setActivePvpRoom(null);
    }
  }, [isUserLoggedIn]);

  const loadHostedOpenSummary = useCallback(async () => {
    if (!isUserLoggedIn) {
      setHostedOpenSummary(null);
      return;
    }
    try {
      const s = await pvpService.getHostedOpenAsyncSummary();
      setHostedOpenSummary(s);
    } catch {
      setHostedOpenSummary(null);
    }
  }, [isUserLoggedIn]);

  const atHostedRoomLimit = useMemo(
    () =>
      hostedOpenSummary != null &&
      hostedOpenSummary.openCount >= hostedOpenSummary.maxOpenRooms,
    [hostedOpenSummary]
  );

  const hostedOpenQuizIdSet = useMemo(
    () => new Set(hostedOpenSummary?.openQuizIds ?? []),
    [hostedOpenSummary?.openQuizIds]
  );

  const createModalSelectedQuiz = useMemo(
    () => createModalQuizzes.find((q) => q.id === createForm.quizId),
    [createModalQuizzes, createForm.quizId]
  );

  const createModalPlayModeBands = useMemo(() => {
    if (!createModalSelectedQuiz) return [];
    if (isStopQuizFormat(createModalSelectedQuiz) || isGeniusQuizFormat(createModalSelectedQuiz))
      return ['Medium'];
    return playModeChoices(createModalSelectedQuiz);
  }, [createModalSelectedQuiz]);

  const createModalSelectedDifficultyLocked = useMemo(() => {
    if (!createModalSelectedQuiz || !createForm.playDifficulty.trim()) return false;
    return isPlayBandCareerLocked(createModalSelectedQuiz, createForm.playDifficulty);
  }, [createModalSelectedQuiz, createForm.playDifficulty]);

  const createModalRoomFieldsReady =
    !!createForm.quizId &&
    createModalPlayModeBands.length > 0 &&
    !!createForm.playDifficulty.trim() &&
    !createModalSelectedDifficultyLocked;

  // Pagination and filters state
  const [filters, setFilters] = useState<PvPRoomFilters>(() => ({
    page: 1,
    pageSize: SOLO_PVP_LOBBY_DEFAULT_PAGE_SIZE,
    search: '',
    difficulty: '',
  }));
  const [pagination, setPagination] = useState({
    totalCount: 0,
    page: 1,
    pageSize: SOLO_PVP_LOBBY_DEFAULT_PAGE_SIZE,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  /** Below md: funnel toggles gray lobby filters card */
  const [mobilePvpFiltersOpen, setMobilePvpFiltersOpen] = useState(false);
  /** Viewport estreito: 1 ou 2 cards por linha (antes do breakpoint md); persistido no localStorage */
  const [mobileCardCols, setMobileCardCols] = useState<1 | 2>(() => {
    if (typeof window === 'undefined') return 1;
    try {
      const v = localStorage.getItem(MOBILE_QUIZ_CARD_COLS_STORAGE_KEY);
      if (v === '2') return 2;
    } catch {
      /* ignore */
    }
    return 1;
  });

  useEffect(() => {
    try {
      localStorage.setItem(MOBILE_QUIZ_CARD_COLS_STORAGE_KEY, String(mobileCardCols));
    } catch {
      /* ignore */
    }
  }, [mobileCardCols]);

  useEffect(() => {
    loadRooms();
  }, [filters]);

  useEffect(() => {
    if (!showCreateModal) return;

    let cancelled = false;
    (async () => {
      setCreateModalQuizzesLoading(true);
      try {
        const list = (await quizService.getActiveQuizzesAllPages({
          allLanguages: true,
        })) as Quiz[];
        if (!cancelled) {
          setCreateModalQuizzes(list);
        }
      } catch (error) {
        if (!cancelled) {
          setCreateModalQuizzes([]);
        }
      } finally {
        if (!cancelled) {
          setCreateModalQuizzesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showCreateModal]);

  useEffect(() => {
    void loadActivePvpRoom();
    void loadHostedOpenSummary();
  }, [loadActivePvpRoom, loadHostedOpenSummary]);

  useEffect(() => {
    const code = pvpJoinCode?.trim();
    if (!code) {
      setInviteJoinModal((prev) => {
        if (prev.source === 'lobby') return prev;
        return { open: false, loading: false, error: null, preview: null, source: null };
      });
      return;
    }
    let cancelled = false;
    setInviteJoinModal({ open: true, loading: true, error: null, preview: null, source: 'url' });
    void pvpService
      .getAsyncPvpInvitePreview(code)
      .then((preview) => {
        if (cancelled) return;
        setInviteJoinModal({ open: true, loading: false, error: null, preview, source: 'url' });
      })
      .catch(() => {
        if (cancelled) return;
        setInviteJoinModal({
          open: true,
          loading: false,
          error: i18n.t('pvp.inviteNotAvailable', {
            defaultValue: 'This invite is no longer available.',
          }),
          preview: null,
          source: 'url',
        });
      });
    return () => {
      cancelled = true;
    };
    // Only `pvpJoinCode`. `t` / i18n must not be deps — unstable refs → infinite update loop.
  }, [pvpJoinCode]);

  useEffect(() => {
    if (!isUserLoggedIn) {
      setMyUserId(null);
      return;
    }
    void (async () => {
      try {
        const u = await authService.getProfile();
        setMyUserId(u.id);
      } catch {
        setMyUserId(null);
      }
    })();
  }, [isUserLoggedIn]);

  // Refresh rooms when component becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadRooms();
        void loadActivePvpRoom();
        void loadHostedOpenSummary();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadActivePvpRoom, loadHostedOpenSummary]);

  const getRoomInviteUrl = (room: PvPRoom) => {
    const seg = room.roomCode?.trim() || room.id;
    return `${getQuizterSiteUrl()}/pvp-quiz/${seg}`;
  };

  const handleCopyPvpRoomInvite = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(
        i18n.t('pvp.inviteLinkCopied', { defaultValue: 'Invite link copied.' })
      );
    } catch {
      toast.error(i18n.t('pvp.inviteLinkCopyFailed', { defaultValue: 'Could not copy link.' }));
    }
  };

  const handleSharePvpRoomInvite = async (url: string, room: PvPRoom) => {
    const quizLabel = getQuizFullDisplayCaption(
      {
        title: room.quizTitle,
        titleEn: room.quizTitleEn,
        format: room.quizFormat,
        theme: room.quizTheme,
        themeEn: room.quizThemeEn,
      },
      i18n.language
    );
    try {
      const result = await sharePvpInviteLink({
        title: i18n.t('pvp.shareInviteTitle', { defaultValue: 'Quizter Club — PvP invite' }),
        text: i18n.t('pvp.shareInviteText', {
          quiz: quizLabel,
          defaultValue: 'Join my PvP match for {{quiz}}:',
        }),
        url,
      });
      if (result === 'shared') {
        toast.success(
          i18n.t('pvp.shareInviteOpened', { defaultValue: 'Share sheet opened.' })
        );
      }
    } catch {
      try {
        await navigator.clipboard.writeText(url);
        toast.success(
          i18n.t('pvp.inviteLinkCopied', { defaultValue: 'Invite link copied.' })
        );
      } catch {
        toast.error(
          i18n.t('pvp.shareInviteFailed', { defaultValue: 'Could not share or copy link.' })
        );
      }
    }
  };

  const loadRooms = async () => {
    try {
      setIsLoading(true);
      const response = await pvpService.getPvPRooms(filters);
      setRooms(response.rooms);
      setPagination({
        totalCount: response.totalCount,
        page: response.page,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        hasNextPage: response.hasNextPage,
        hasPreviousPage: response.hasPreviousPage
      });
      setError(null);
    } catch (error) {
      setError(t('pvp.failedToLoadRooms', 'Could not load rooms.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setFilters(prev => ({ ...prev, pageSize, page: 1 }));
  };

  const handleSearchChange = (search: string) => {
    setFilters(prev => ({ ...prev, search, page: 1 }));
  };

  const handleDifficultyChange = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      difficulty: value === 'all' ? '' : value,
      page: 1,
    }));
  };

  const handleCreateRoom = async () => {
    if (!isUserLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    if (createRoomInFlightRef.current || creating) {
      return;
    }

    if (!createForm.name.trim() || !createForm.quizId || !createForm.playDifficulty.trim()) {
      return;
    }

    const selectedQuiz = createModalQuizzes.find((q) => q.id === createForm.quizId);
    if (selectedQuiz && isQuizCareerLocked(selectedQuiz)) {
      setPvpQuizLockedTitle(getQuizFullDisplayCaption(selectedQuiz, i18n.language));
      return;
    }

    createRoomInFlightRef.current = true;
    try {
      setCreating(true);
      const room = await pvpService.createAsyncPvPRoom({
        name: createForm.name.trim(),
        quizId: createForm.quizId,
        playDifficulty: createForm.playDifficulty.trim(),
      });
      setShowCreateModal(false);
      setCreateForm({ name: '', quizId: '', playDifficulty: '' });

      navigate(`/pvp-quiz/${room.roomCode?.trim() || room.id}`, { state: { fromPvpLobby: true } });
      await loadHostedOpenSummary();
      await loadActivePvpRoom();
    } catch (error: any) {
      if (isQuizLockedApiError(error)) {
        const selected = createModalQuizzes.find((q) => q.id === createForm.quizId);
        setPvpQuizLockedTitle(
          selected
            ? getQuizFullDisplayCaption(selected, i18n.language)
            : t('quiz.blockedTitle', 'Quiz locked')
        );
        return;
      }
      if (isPlayDifficultyLockedApiError(error)) {
        toast.error(t('quiz.playDifficultyLockedBody'));
        return;
      }
      if (error.response?.status === 400) {
        const msg = error.response?.data?.message;
        if (typeof msg === 'string') toast.error(msg);
        await loadHostedOpenSummary();
        return;
      }
      if (error.response?.status === 409) {
        const id = error.response?.data?.activeRoomId as string | undefined;
        if (id) {
          navigate(`/pvp-quiz/${id}`, { state: { fromPvpLobby: true } });
        }
        await loadActivePvpRoom();
        await loadHostedOpenSummary();
        return;
      }
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors ||
        error.message ||
        t('pvp.errorCreatingRoom', 'Error creating room');
      const raw =
        typeof errorMessage === 'string' ? errorMessage : t('pvp.errorCreatingRoom', 'Error creating room');
      if (isInsufficientLivesMessage(raw)) {
        setLivesGateMessage('');
        setLivesGateOpen(true);
      }
    } finally {
      createRoomInFlightRef.current = false;
      setCreating(false);
    }
  };

  const closeInviteJoinModal = () => {
    setInviteJoinModal((prev) => {
      if (prev.source === 'url') {
        onPvpJoinConsumed?.();
      }
      return { open: false, loading: false, error: null, preview: null, source: null };
    });
  };

  /** Abre o mesmo modal do convite <c>?pvpJoin=</c>, com dados já listados na Arena. */
  const openJoinConfirmationFromLobby = (room: PvPRoom) => {
    const joinableStatus = room.status === 'Waiting' || room.status === 'AwaitingOpponent';
    const isHostMine = !!(myUserId && room.hostId === myUserId);
    const inThisRoom = room.isUserInRoom || activePvpRoom?.id === room.id;
    const blockedInOtherRoom = !!(activePvpRoom && activePvpRoom.id !== room.id);
    if (!joinableStatus || inThisRoom || blockedInOtherRoom || isHostMine) return;

    setInviteJoinModal({
      open: true,
      loading: false,
      error: null,
      preview: {
        roomId: room.id,
        roomCode: room.roomCode?.trim() ?? '',
        name: room.name,
        quizTitle: room.quizTitle,
        quizTitleEn: room.quizTitleEn,
        quizTheme: room.quizTheme,
        quizThemeEn: room.quizThemeEn,
        quizFormat: room.quizFormat,
        quizDifficulty: room.quizDifficulty,
        hostNickname: room.hostNickname || room.hostName || '—',
        hostIsVip: room.hostIsVip === true,
        status: room.status,
      },
      source: 'lobby',
    });
  };

  const joinRoom = async (roomId: string, pathSegment?: string, onJoined?: () => void) => {
    try {
      setJoining(true);
      await pvpService.joinAsyncPvPRoom(roomId);
      onJoined?.();
      navigate(`/pvp-quiz/${pathSegment?.trim() || roomId}`, { state: { fromPvpLobby: true } });
    } catch (error: any) {
      if (error.response?.status === 409) {
        const id = error.response?.data?.activeRoomId as string | undefined;
        if (id) {
          navigate(`/pvp-quiz/${id}`, { state: { fromPvpLobby: true } });
        }
        await loadActivePvpRoom();
        return;
      }
      const serverMsg = error.response?.data?.message;
      const raw =
        typeof serverMsg === 'string' && serverMsg.trim()
          ? serverMsg
          : t('pvp.errorJoiningRoom', 'Error joining room');
      if (isInsufficientLivesMessage(raw)) {
        setLivesGateMessage('');
        setLivesGateOpen(true);
      }
    } finally {
      setJoining(false);
    }
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    if (onLoginSuccess) {
      onLoginSuccess();
    }
    void loadActivePvpRoom();
    void loadHostedOpenSummary();
  };

  const openCreateRoomModal = () => {
    setCreateForm({ name: '', quizId: '', playDifficulty: '' });
    setShowCreateModal(true);
  };

  const handleCreateButtonClick = async () => {
    if (!isUserLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    if (atHostedRoomLimit) {
      toast.error(
        t('pvp.maxHostedRoomsReached', 'You already have the maximum open rooms ({{max}}). Finish a match or wait for a room to close before creating another.').replace(
          '{{max}}',
          String(hostedOpenSummary?.maxOpenRooms ?? 3)
        )
      );
      return;
    }
    setCreateLivesCheckLoading(true);
    try {
      const user = await authService.getProfile();
      if (!isUnlimitedUserType(user.userType) && (user.lives ?? 0) < 1) {
        setLivesGateMessage('');
        setLivesGateOpen(true);
        return;
      }
      try {
        const inv = await inventoryService.getMine();
        if (shouldPromptXpPotionReminder(user, inv.slots)) {
          setXpPotionPvpGateOpen(true);
          return;
        }
        if (
          shouldPromptHealthPotionReminder(user) &&
          inventorySlotsHaveUnusedHealthPotion(inv.slots)
        ) {
          setHealthReminderPendingPvpCreate(true);
          setHealthPotionGateOpen(true);
          return;
        }
      } catch {
        /* seguir sem gate */
      }
      openCreateRoomModal();
    } catch {
      return;
    } finally {
      setCreateLivesCheckLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Waiting':
        return <Badge bg="warning">{t('pvp.waiting', 'Waiting')}</Badge>;
      case 'AwaitingOpponent':
        return <Badge bg="info">{t('pvp.awaitingOpponent', 'Waiting for opponent')}</Badge>;
      case 'Draft':
        return <Badge bg="secondary">{t('pvp.draft', 'Draft')}</Badge>;
      case 'Playing':
        return <Badge bg="primary">{t('pvp.playing', 'Playing')}</Badge>;
      case 'Closed':
        return <Badge bg="success">{t('pvp.closed', 'Closed')}</Badge>;
      case 'Cancelled':
        return <Badge bg="dark">{t('pvp.cancelled', 'Cancelled')}</Badge>;
      case 'Finished':
        return <Badge bg="success">{t('pvp.finished', 'Finished')}</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatQuizDifficulty = (difficulty: string) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy':
        return t('quiz.easy', 'Easy');
      case 'medium':
        return t('quiz.medium', 'Medium');
      case 'hard':
        return t('quiz.hard', 'Hard');
      default:
        return difficulty;
    }
  };

  /** Mesmo padrão visual do card solo (QuizList). */
  const getDifficultyBadge = (difficulty: string) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy':
        return <Badge bg="success">{t('quiz.easy', 'Easy')}</Badge>;
      case 'medium':
        return <Badge bg="warning">{t('quiz.medium', 'Medium')}</Badge>;
      case 'hard':
        return <Badge bg="danger">{t('quiz.hard', 'Hard')}</Badge>;
      default:
        return <Badge bg="secondary">{difficulty?.trim() || '—'}</Badge>;
    }
  };

  /** Mesmo padrão visual do card solo (QuizList). */
  const getLanguageFlag = (language: string | undefined) => {
    switch (language) {
      case 'en':
        return <span className="fi fi-us" style={{ borderRadius: '3px' }} />;
      case 'pt':
        return <span className="fi fi-br" style={{ borderRadius: '3px' }} />;
      default:
        return null;
    }
  };

  return (
    <Container fluid className="quiz-list-page">
      <Row className="mb-0">
        <Col xs={12} className="px-3">
          <div className="quiz-list-hero-stack quiz-list-hero-stack--mobile-filter-corner">
            <Button
              type="button"
              variant={mobilePvpFiltersOpen ? 'secondary' : 'outline-secondary'}
              size="sm"
              className="quiz-list-mobile-filters-toggle-btn d-md-none rounded-3 shadow-sm"
              aria-pressed={mobilePvpFiltersOpen}
              onClick={(e) => {
                setMobilePvpFiltersOpen((o) => !o);
                const el = e.currentTarget;
                window.setTimeout(() => {
                  el.blur();
                }, 0);
              }}
              aria-expanded={mobilePvpFiltersOpen}
              aria-controls="pvp-lobby-mobile-filters-panel"
              title={t('quiz.toggleQuizFiltersAria', 'Search and filter quizzes')}
              aria-label={t('quiz.toggleQuizFiltersAria', 'Search and filter quizzes')}
            >
              <i className={`bi bi-funnel ${mobilePvpFiltersOpen ? 'text-white' : ''}`} aria-hidden />
            </Button>
            <div className="quiz-list-header">
              <h1>🏆 {t('pvp.arenaTitle', 'PvP Arena')}</h1>
              <p className="text-muted">{t('pvp.arenaSubtitle', 'Compete with other players!')}</p>
            </div>
            <div className="quiz-list-hero-cta-row">
              <span className="quiz-list-quick-match-wrap">
                <Button
                  type="button"
                  variant="success"
                  size="lg"
                  className="quiz-list-quick-match-btn fw-semibold px-4 py-3 rounded-3 shadow-sm"
                  onClick={() => void handleCreateButtonClick()}
                  disabled={atHostedRoomLimit || createLivesCheckLoading}
                  title={
                    atHostedRoomLimit
                      ? t('pvp.maxHostedRoomsReached', 'You already have the maximum open rooms ({{max}}). Cancel or finish one before creating another.').replace(
                          '{{max}}',
                          String(hostedOpenSummary?.maxOpenRooms ?? 3)
                        )
                      : undefined
                  }
                  aria-label={t('pvp.create', 'Create')}
                >
                  {createLivesCheckLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                      {t('common.loading', 'Loading...')}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-plus-circle me-2" aria-hidden />
                      {t('pvp.create', 'Create')}
                    </>
                  )}
                </Button>
              </span>
            </div>
          </div>
        </Col>
      </Row>

      {/* Filters — mesmo padrão do solo (mobile: collapse; desktop: uma linha) */}
      <Row className="mb-0 mb-md-4">
        <Col>
          <div className="d-md-none">
            <Collapse in={mobilePvpFiltersOpen}>
              <div id="pvp-lobby-mobile-filters-panel">
                <Card>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('pvp.searchRooms', 'Search Rooms')}</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder={t(
                          'pvp.searchPlaceholder',
                          'Search by room name, quiz title, or theme...'
                        )}
                        value={filters.search || ''}
                        onChange={(e) => handleSearchChange(e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('pvp.filterByDifficulty', 'Difficulty')}</Form.Label>
                      <Form.Select
                        value={filters.difficulty?.trim() ? filters.difficulty : 'all'}
                        onChange={(e) => handleDifficultyChange(e.target.value)}
                      >
                        <option value="all">{t('pvp.allDifficulties', 'All difficulties')}</option>
                        <option value="Easy">{t('quiz.easy', 'Easy')}</option>
                        <option value="Medium">{t('quiz.medium', 'Medium')}</option>
                        <option value="Hard">{t('quiz.hard', 'Hard')}</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('pvp.itemsPerPage', 'Items per page')}</Form.Label>
                      <Form.Select
                        value={filters.pageSize || SOLO_PVP_LOBBY_DEFAULT_PAGE_SIZE}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      >
                        {SOLO_PVP_LOBBY_PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Button
                      variant="outline-secondary"
                      className="w-100 pvp-clear-filters-btn"
                      onClick={() => {
                        setFilters({
                          page: 1,
                          pageSize: SOLO_PVP_LOBBY_DEFAULT_PAGE_SIZE,
                          search: '',
                          difficulty: '',
                        });
                      }}
                    >
                      {t('pvp.clearFilters', 'Clear Filters')}
                    </Button>
                  </Card.Body>
                </Card>
              </div>
            </Collapse>
          </div>

          <div className="d-none d-md-block">
            <Card>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{t('pvp.searchRooms', 'Search Rooms')}</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder={t(
                          'pvp.searchPlaceholder',
                          'Search by room name, quiz title, or theme...'
                        )}
                        value={filters.search || ''}
                        onChange={(e) => handleSearchChange(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>{t('pvp.filterByDifficulty', 'Difficulty')}</Form.Label>
                      <Form.Select
                        value={filters.difficulty?.trim() ? filters.difficulty : 'all'}
                        onChange={(e) => handleDifficultyChange(e.target.value)}
                      >
                        <option value="all">{t('pvp.allDifficulties', 'All difficulties')}</option>
                        <option value="Easy">{t('quiz.easy', 'Easy')}</option>
                        <option value="Medium">{t('quiz.medium', 'Medium')}</option>
                        <option value="Hard">{t('quiz.hard', 'Hard')}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>{t('pvp.itemsPerPage', 'Items per page')}</Form.Label>
                      <Form.Select
                        value={filters.pageSize || SOLO_PVP_LOBBY_DEFAULT_PAGE_SIZE}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      >
                        {SOLO_PVP_LOBBY_PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <Button
                      variant="outline-secondary"
                      className="pvp-clear-filters-btn w-100 text-nowrap"
                      onClick={() => {
                        setFilters({
                          page: 1,
                          pageSize: SOLO_PVP_LOBBY_DEFAULT_PAGE_SIZE,
                          search: '',
                          difficulty: '',
                        });
                      }}
                    >
                      {t('pvp.clearFilters', 'Clear Filters')}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Results Info — mobile: «Mostrando» linha cheia; «Página» só à direita na linha dos toggles */}
      <Row className="mb-3">
        <Col>
          <div className="d-md-none">
            <p className="text-muted mb-0 text-center w-100 mb-2">
              {t('pvp.showing', 'Showing {{start}} to {{end}} of {{total}} rooms')
                .replace('{{start}}', String(((pagination.page - 1) * pagination.pageSize) + 1))
                .replace('{{end}}', String(Math.min(pagination.page * pagination.pageSize, pagination.totalCount)))
                .replace('{{total}}', String(pagination.totalCount))}
            </p>
            {pagination.totalCount > 0 && (
              <div className="d-flex justify-content-between align-items-center">
                <div
                  className="btn-group btn-group-sm pvp-lobby-card-layout-toggle d-flex"
                  role="group"
                  aria-label={t('pvp.mobileGridLayoutAria', 'Layout of room cards on small screens')}
                >
                  <Button
                    type="button"
                    variant={mobileCardCols === 1 ? 'primary' : 'outline-secondary'}
                    className="px-2 py-1"
                    onClick={() => setMobileCardCols(1)}
                    aria-pressed={mobileCardCols === 1}
                    title={t('pvp.mobileGridOnePerRow', 'One card per row')}
                  >
                    <i className="bi bi-list-ul" aria-hidden />
                    <span className="visually-hidden">
                      {t('pvp.mobileGridOnePerRow', 'One card per row')}
                    </span>
                  </Button>
                  <Button
                    type="button"
                    variant={mobileCardCols === 2 ? 'primary' : 'outline-secondary'}
                    className="px-2 py-1"
                    onClick={() => setMobileCardCols(2)}
                    aria-pressed={mobileCardCols === 2}
                    title={t('pvp.mobileGridTwoPerRow', 'Two cards per row')}
                  >
                    <i className="bi bi-grid-3x2-gap" aria-hidden />
                    <span className="visually-hidden">
                      {t('pvp.mobileGridTwoPerRow', 'Two cards per row')}
                    </span>
                  </Button>
                </div>
                <p className="text-muted mb-0 text-nowrap">
                  {t('pvp.pageOf', 'Page {{current}} of {{total}}')
                    .replace('{{current}}', String(pagination.page))
                    .replace('{{total}}', String(pagination.totalPages))}
                </p>
              </div>
            )}
          </div>
          <div className="d-none d-md-flex justify-content-between align-items-center flex-wrap gap-2 w-100">
            <p className="text-muted mb-0 flex-grow-1 min-w-0">
              {t('pvp.showing', 'Showing {{start}} to {{end}} of {{total}} rooms')
                .replace('{{start}}', String(((pagination.page - 1) * pagination.pageSize) + 1))
                .replace('{{end}}', String(Math.min(pagination.page * pagination.pageSize, pagination.totalCount)))
                .replace('{{total}}', String(pagination.totalCount))}
            </p>
            <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-md-auto">
              {pagination.totalCount > 0 && (
                <p className="text-muted mb-0 text-nowrap">
                  {t('pvp.pageOf', 'Page {{current}} of {{total}}')
                    .replace('{{current}}', String(pagination.page))
                    .replace('{{total}}', String(pagination.totalPages))}
                </p>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Rooms Grid */}
      {isLoading ? (
        <Row>
          <Col className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
            </Spinner>
          </Col>
        </Row>
      ) : rooms.length > 0 ? (
        <>
          <Row className="g-2 g-md-3">
            {rooms.map((room) => {
              const inThisRoom = room.isUserInRoom || activePvpRoom?.id === room.id;
              const blockedInOtherRoom = !!(activePvpRoom && activePvpRoom.id !== room.id);
              const isHostMine = !!(myUserId && room.hostId === myUserId);
              const joinableStatus = room.status === 'Waiting' || room.status === 'AwaitingOpponent';
              const canJoin =
                joinableStatus && !inThisRoom && !blockedInOtherRoom && !isHostMine;
              /** Esconder CTA só quando já está na sala como não-host (ex.: oponente). Salas suas sempre mostram aviso. */
              const suppressLobbyPrimaryButton = inThisRoom && !isHostMine;
              const showLobbyExpiryCountdown =
                room.status === 'AwaitingOpponent' && !!room.publishedAt;
              /** Mobile 1 col: mesmo layout visual do desktop (estado no topo, countdown ao lado do botão). */
              const wideMobileCard = !isMdUp && mobileCardCols === 1;
              /** Mobile 2 col: countdown na meta — aproxima badge “Aguardando oponente” do tempo (metade do mb+pt padrão). */
              const tightMetaToActionsMobileGap =
                showLobbyExpiryCountdown && !isMdUp && !wideMobileCard;
              const roomLangFlag = getLanguageFlag(room.quizLanguage);
              const roomQuizForCard = {
                title: room.quizTitle,
                titleEn: room.quizTitleEn,
                format: room.quizFormat,
                theme: room.quizTheme,
                themeEn: room.quizThemeEn,
              };
              const roomDisplayTheme = getQuizDisplayTheme(roomQuizForCard, i18n.language);
              return (
              <Col
                key={room.id}
                xs={mobileCardCols === 2 ? 6 : 12}
                md={6}
                lg={4}
                className="mb-3 mb-md-4"
              >
                <Card
                  className={`quiz-card pvp-room-card h-100${
                    wideMobileCard ? ' pvp-room-card--wide-mobile' : ''
                  }`}
                >
                  <Card.Body className="d-flex flex-column">
                    <div className="mb-2 pvp-room-card-header">
                      <div className="pvp-room-card-status-row">
                        <div className="pvp-room-card-status-row__diff">
                          {getDifficultyBadge(room.quizDifficulty)}
                        </div>
                        <div
                          className={`pvp-room-card-status-row__status ${
                            wideMobileCard ? 'd-flex' : 'd-none d-md-flex'
                          }`}
                        >
                          {getStatusBadge(room.status)}
                        </div>
                        <div className="pvp-room-card-invite-actions">
                          <div className="d-flex align-items-center justify-content-end flex-nowrap gap-0 pvp-room-card-invite-actions-inner">
                            <Button
                              variant="link"
                              className="p-1 text-primary"
                              type="button"
                              aria-label={i18n.t('pvp.copyInviteLink', { defaultValue: 'Copy link' })}
                              title={i18n.t('pvp.copyInviteLink', { defaultValue: 'Copy link' })}
                              onClick={() => void handleCopyPvpRoomInvite(getRoomInviteUrl(room))}
                            >
                              <i className="bi bi-link-45deg" aria-hidden />
                            </Button>
                            <Button
                              variant="link"
                              className="p-1 text-primary"
                              type="button"
                              aria-label={i18n.t('pvp.shareInviteLink', { defaultValue: 'Share invite link' })}
                              title={i18n.t('pvp.shareInviteLink', { defaultValue: 'Share invite link' })}
                              onClick={() => void handleSharePvpRoomInvite(getRoomInviteUrl(room), room)}
                            >
                              <i className="bi bi-share" aria-hidden />
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center quiz-card-title-flag-row pvp-room-card-name-flag-row">
                        <div className="quiz-card-lang-cell">
                          {roomLangFlag}
                        </div>
                        <div className="flex-grow-1 min-w-0 text-center px-1">
                          <Card.Title as="h5" className="quiz-title mb-0 text-break">
                            {room.name}
                          </Card.Title>
                        </div>
                        <div className="quiz-card-lang-cell quiz-card-lang-cell--balance" aria-hidden="true" />
                      </div>
                    </div>
                    <Card.Text
                      as="div"
                      className={`text-muted text-center small pvp-room-card-meta ${
                        tightMetaToActionsMobileGap ? 'mb-1' : 'mb-2'
                      }`}
                    >
                      <div className="d-flex flex-column align-items-center gap-2 pvp-room-card-meta-stack">
                        <div className="mb-0 w-100 pvp-room-card-meta-quiz-title d-flex flex-column align-items-center gap-1">
                          <div className="d-flex align-items-center justify-content-center gap-1 flex-wrap w-100">
                            {room.quizIsUnlockable ? (
                              <i
                                className="bi bi-lock-fill text-secondary flex-shrink-0"
                                title={i18n.t('quiz.unlockableListHint', {
                                  defaultValue: 'Unlockable quiz — shown with lock in lobbies',
                                })}
                                aria-hidden
                              />
                            ) : null}
                            <span className="text-break">
                              {getQuizDisplayTitle(roomQuizForCard, i18n.language)}
                            </span>
                          </div>
                          {roomDisplayTheme ? (
                            <span className="text-break small text-muted text-center w-100">
                              {roomDisplayTheme}
                            </span>
                          ) : null}
                        </div>
                        <div
                          className={`d-flex ${
                            wideMobileCard ? 'flex-row' : 'flex-column'
                          } flex-md-row align-items-center justify-content-center gap-2 flex-wrap mb-0 pvp-room-card-participants-host`}
                        >
                          <span className="d-inline-flex align-items-center">
                            <i className="bi bi-people me-1" aria-hidden />
                            {t('pvp.participantsCount', '{{current}}/{{max}} participants')
                              .replace('{{current}}', String(room.participantCount))
                              .replace('{{max}}', String(room.maxParticipants))}
                          </span>
                          <span
                            className={`pvp-room-card-meta-sep text-muted opacity-50 ${
                              wideMobileCard ? 'd-inline' : 'd-none d-md-inline'
                            }`}
                            aria-hidden
                          >
                            ·
                          </span>
                          <span className="d-inline-flex align-items-center flex-wrap gap-1">
                            <i className="bi bi-person flex-shrink-0" aria-hidden />
                            <span className="text-nowrap">
                              {t('pvp.hostLabel', 'Host:')}
                            </span>
                            <span
                              className={
                                room.hostIsVip === true ? 'vip-subscriber-nickname' : undefined
                              }
                            >
                              {room.hostNickname || room.hostName || '—'}
                            </span>
                            {room.hostIsVip === true && (
                              <i className="bi bi-gem text-warning" title="VIP" aria-hidden />
                            )}
                          </span>
                        </div>
                        {showLobbyExpiryCountdown && !isMdUp && !wideMobileCard ? (
                          <div className="d-flex justify-content-center align-items-center gap-1 mb-0 pvp-room-card-lobby-expiry-meta-line">
                            <PvPRoomLobbyExpiryCountdown
                              publishedAt={room.publishedAt}
                              status={room.status}
                            />
                          </div>
                        ) : null}
                      </div>
                    </Card.Text>
                    <div
                      className={`mt-auto d-flex flex-column align-items-center pvp-room-card-actions ${
                        tightMetaToActionsMobileGap ? 'pt-1' : 'pt-2'
                      }`}
                    >
                      <div
                        className={`${
                          wideMobileCard ? 'd-none' : 'd-flex d-md-none'
                        } justify-content-center w-100 mb-2 pvp-room-card-status-mobile`}
                      >
                        {getStatusBadge(room.status)}
                      </div>
                      {showLobbyExpiryCountdown ? (
                        isMdUp || wideMobileCard ? (
                        suppressLobbyPrimaryButton ? (
                        <div className="w-100 d-flex justify-content-center align-items-center py-1">
                          <PvPRoomLobbyExpiryCountdown
                            publishedAt={room.publishedAt}
                            status={room.status}
                          />
                        </div>
                        ) : (
                        <div className="w-100 pvp-room-card-actions-row">
                          <div className="pvp-room-card-actions-lead" aria-hidden />
                          <div className="pvp-room-card-actions-cta">
                            <Button
                              variant={inThisRoom || isHostMine ? 'secondary' : 'primary'}
                              className="quiz-card-start-btn"
                              onClick={() => openJoinConfirmationFromLobby(room)}
                              disabled={!canJoin || joining}
                              title={
                                isHostMine
                                  ? t('pvp.cannotJoinOwnRoom')
                                  : blockedInOtherRoom
                                    ? t('pvp.cannotJoinAnotherRoomToast', 'You are already in a PvP room. Leave it before joining another.')
                                    : undefined
                              }
                            >
                              {isHostMine
                                ? t('pvp.cannotJoinOwnRoom')
                                : inThisRoom
                                  ? t('pvp.alreadyInRoom', 'Already in room')
                                  : blockedInOtherRoom
                                    ? t('pvp.cannotJoinWhileInOtherRoom', 'Leave your current room first')
                                  : !joinableStatus
                                    ? t('pvp.roomNotJoinable', 'Not open to join')
                                    : joining
                                      ? (
                                        <>
                                          <Spinner animation="border" size="sm" className="me-2" />
                                          {t('pvp.joining', 'Joining...')}
                                        </>
                                        )
                                      : (
                                        <>
                                          <i className="bi bi-play-circle me-2" aria-hidden />
                                          {t('pvp.joinRoom', 'Join Room')}
                                        </>
                                        )}
                            </Button>
                          </div>
                          <div className="pvp-room-card-actions-trailing">
                            <PvPRoomLobbyExpiryCountdown
                              publishedAt={room.publishedAt}
                              status={room.status}
                            />
                          </div>
                        </div>
                        )
                        ) : suppressLobbyPrimaryButton ? null : (
                        <div className="w-100 d-flex justify-content-center">
                            <Button
                              variant={inThisRoom || isHostMine ? 'secondary' : 'primary'}
                              className="quiz-card-start-btn"
                              onClick={() => openJoinConfirmationFromLobby(room)}
                              disabled={!canJoin || joining}
                              title={
                                isHostMine
                                  ? t('pvp.cannotJoinOwnRoom')
                                  : blockedInOtherRoom
                                    ? t('pvp.cannotJoinAnotherRoomToast', 'You are already in a PvP room. Leave it before joining another.')
                                    : undefined
                              }
                            >
                              {isHostMine
                                ? t('pvp.cannotJoinOwnRoom')
                                : inThisRoom
                                  ? t('pvp.alreadyInRoom', 'Already in room')
                                  : blockedInOtherRoom
                                    ? t('pvp.cannotJoinWhileInOtherRoom', 'Leave your current room first')
                                  : !joinableStatus
                                    ? t('pvp.roomNotJoinable', 'Not open to join')
                                    : joining
                                      ? (
                                        <>
                                          <Spinner animation="border" size="sm" className="me-2" />
                                          {t('pvp.joining', 'Joining...')}
                                        </>
                                        )
                                      : (
                                        <>
                                          <i className="bi bi-play-circle me-2" aria-hidden />
                                          {t('pvp.joinRoom', 'Join Room')}
                                        </>
                                        )}
                            </Button>
                        </div>
                        )
                      ) : suppressLobbyPrimaryButton ? null : (
                        <>
                          <Button
                            variant={inThisRoom || isHostMine ? 'secondary' : 'primary'}
                            className="quiz-card-start-btn"
                            onClick={() => openJoinConfirmationFromLobby(room)}
                            disabled={!canJoin || joining}
                            title={
                              isHostMine
                                ? t('pvp.cannotJoinOwnRoom')
                                : blockedInOtherRoom
                                  ? t('pvp.cannotJoinAnotherRoomToast', 'You are already in a PvP room. Leave it before joining another.')
                                  : undefined
                            }
                          >
                            {isHostMine
                              ? t('pvp.cannotJoinOwnRoom')
                              : inThisRoom
                                ? t('pvp.alreadyInRoom', 'Already in room')
                                : blockedInOtherRoom
                                  ? t('pvp.cannotJoinWhileInOtherRoom', 'Leave your current room first')
                                : !joinableStatus
                                  ? t('pvp.roomNotJoinable', 'Not open to join')
                                  : joining
                                    ? (
                                      <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        {t('pvp.joining', 'Joining...')}
                                      </>
                                      )
                                    : (
                                      <>
                                        <i className="bi bi-play-circle me-2" aria-hidden />
                                        {t('pvp.joinRoom', 'Join Room')}
                                      </>
                                      )}
                          </Button>
                        </>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
            })}
          </Row>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Row className="mt-4">
              <Col className="d-flex justify-content-center">
                <Pagination>
                  <Pagination.First 
                    disabled={!pagination.hasPreviousPage}
                    onClick={() => handlePageChange(1)}
                  />
                  <Pagination.Prev 
                    disabled={!pagination.hasPreviousPage}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  />
                  
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(pagination.totalPages - 4, pagination.page - 2)) + i;
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === pagination.page}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}
                  
                  <Pagination.Next 
                    disabled={!pagination.hasNextPage}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  />
                  <Pagination.Last 
                    disabled={!pagination.hasNextPage}
                    onClick={() => handlePageChange(pagination.totalPages)}
                  />
                </Pagination>
              </Col>
            </Row>
          )}
        </>
      ) : (
        <Row>
          <Col className="text-center py-5">
            <div className="empty-state">
              <i className="bi bi-people text-muted" style={{ fontSize: '4rem' }}></i>
              <h4 className="mt-3">{t('pvp.noRoomsFound', 'No rooms found')}</h4>
              <p className="text-muted">
                {filters.search?.trim() || filters.difficulty?.trim()
                  ? t('pvp.adjustFilters', 'Try adjusting your search criteria or filters.')
                  : t('pvp.noRoomsDescription', 'There are no PvP rooms available at the moment.')
                }
              </p>
            </div>
          </Col>
        </Row>
      )}

      {/* Create Room Modal */}
      <Modal
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false);
          setCreateForm({ name: '', quizId: '', playDifficulty: '' });
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('pvp.createNewRoom', 'Create New PvP Room')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('pvp.selectQuiz', 'Quiz')} {t('pvp.required', '*')}</Form.Label>
              <Form.Select
                value={createForm.quizId}
                onChange={(e) => {
                  const qid = e.target.value;
                  const q = createModalQuizzes.find((x) => x.id === qid);
                  const nextDiff = q
                    ? isStopQuizFormat(q) || isGeniusQuizFormat(q)
                      ? 'Medium'
                      : defaultHighestUnlockedPlayDifficulty(q)
                    : '';
                  setCreateForm((prev) => ({
                    ...prev,
                    quizId: qid,
                    playDifficulty: nextDiff,
                    name: '',
                  }));
                }}
                disabled={createModalQuizzesLoading || createModalQuizzes.length === 0}
                autoComplete="off"
              >
                <option value="">{t('pvp.selectQuizOption', 'Select a quiz')}</option>
                {createModalQuizzes.map((quiz) => {
                  const taken = hostedOpenQuizIdSet.has(quiz.id);
                  const lockPrefix = isQuizCareerLocked(quiz)
                    ? i18n.t('quiz.unlockableListPrefix', { defaultValue: '🔒 ' })
                    : '';
                  return (
                    <option key={quiz.id} value={quiz.id} disabled={taken}>
                      {lockPrefix}
                      {getQuizFullDisplayCaption(quiz, i18n.language)}
                      {taken
                        ? ` — ${t('pvp.quizAlreadyOpenAsHost', 'already in an open room')}`
                        : ''}
                    </option>
                  );
                })}
              </Form.Select>
              {createModalQuizzesLoading && (
                <div className="d-flex align-items-center gap-2 mt-2 text-muted small">
                  <Spinner animation="border" size="sm" role="status" />
                  <span>{t('common.loading', 'Loading...')}</span>
                </div>
              )}
              {!createModalQuizzesLoading && createModalQuizzes.length === 0 && (
                <Alert variant="info" className="mt-2 mb-0 small">
                  {t('pvp.noQuizzesAvailable', 'There are no active quizzes available right now.')}
                </Alert>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('quiz.difficulty', 'Difficulty')} {t('pvp.required', '*')}</Form.Label>
              <Form.Select
                value={createForm.playDifficulty}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, playDifficulty: e.target.value }))
                }
                disabled={
                  createModalQuizzesLoading ||
                  !createForm.quizId ||
                  createModalPlayModeBands.length === 0
                }
                autoComplete="off"
              >
                <option value="">
                  {!createForm.quizId
                    ? t('pvp.selectQuizFirst', 'Select a quiz first')
                    : t('pvp.selectDifficultyOption', 'Select difficulty')}
                </option>
                {createModalPlayModeBands.map((band) => {
                  const locked =
                    !!createModalSelectedQuiz &&
                    isPlayBandCareerLocked(createModalSelectedQuiz, band);
                  const lockPrefix = locked
                    ? i18n.t('quiz.unlockableListPrefix', { defaultValue: '🔒 ' })
                    : '';
                  return (
                    <option key={band} value={band} disabled={locked}>
                      {lockPrefix}
                      {formatQuizDifficulty(band)}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('pvp.roomName', 'Room Name')} {t('pvp.required', '*')}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t('pvp.enterRoomName', 'Enter room name')}
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                maxLength={50}
                disabled={!createModalRoomFieldsReady}
                autoComplete="off"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowCreateModal(false);
              setCreateForm({ name: '', quizId: '', playDifficulty: '' });
            }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button 
            variant="success" 
            onClick={handleCreateRoom}
            disabled={
              creating ||
              atHostedRoomLimit ||
              !createModalRoomFieldsReady ||
              !createForm.name.trim() ||
              hostedOpenQuizIdSet.has(createForm.quizId)
            }
          >
            {creating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t('pvp.creating', 'Creating...')}
              </>
            ) : (
              t('pvp.createRoomButton', 'Create Room')
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <XpPotionReminderModal
        show={xpPotionPvpGateOpen}
        onHide={() => setXpPotionPvpGateOpen(false)}
        onGoToInventory={() => {
          setXpPotionPvpGateOpen(false);
          navigate('/inventory');
        }}
        onContinueWithoutBonus={() => {
          setXpPotionPvpGateOpen(false);
          void (async () => {
            try {
              const u = await authService.getProfile();
              const inv = await inventoryService.getMine();
              if (
                shouldPromptHealthPotionReminder(u) &&
                inventorySlotsHaveUnusedHealthPotion(inv.slots)
              ) {
                setHealthReminderPendingPvpCreate(true);
                setHealthPotionGateOpen(true);
                return;
              }
            } catch {
              /* ignore */
            }
            openCreateRoomModal();
          })();
        }}
      />

      <HealthPotionReminderModal
        show={healthPotionGateOpen}
        onHide={() => {
          setHealthPotionGateOpen(false);
          setHealthReminderPendingPvpCreate(false);
        }}
        onGoToInventory={() => {
          setHealthPotionGateOpen(false);
          setHealthReminderPendingPvpCreate(false);
          navigate('/inventory');
        }}
        onContinueWithoutUsing={() => {
          setHealthPotionGateOpen(false);
          const openCreate = healthReminderPendingPvpCreate;
          setHealthReminderPendingPvpCreate(false);
          if (openCreate) {
            openCreateRoomModal();
          }
        }}
      />

      <LivesGateModal
        show={livesGateOpen}
        message={livesGateMessage || undefined}
        onHide={() => setLivesGateOpen(false)}
      />

      <QuizLockedModal
        show={!!pvpQuizLockedTitle}
        onHide={() => setPvpQuizLockedTitle(null)}
        quizTitle={pvpQuizLockedTitle ?? ''}
        onGoCareer={() => {
          const caption = pvpQuizLockedTitle ?? '';
          setPvpQuizLockedTitle(null);
          setShowCreateModal(false);
          setCreateForm({ name: '', quizId: '', playDifficulty: '' });
          navigate(careerHomePathWithSearch(caption));
        }}
      />

      <Modal
        show={inviteJoinModal.open}
        onHide={closeInviteJoinModal}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t('pvp.inviteJoinTitle', 'Join this PvP room?')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {inviteJoinModal.loading && !inviteJoinModal.error && (
            <div className="text-center py-3">
              <Spinner animation="border" role="status" />
            </div>
          )}
          {inviteJoinModal.error && (
            <Alert variant="warning" className="mb-0">
              {inviteJoinModal.error}
            </Alert>
          )}
          {!inviteJoinModal.loading && inviteJoinModal.preview && (() => {
            const p = inviteJoinModal.preview;
            const invQuizCard = {
              title: p.quizTitle,
              titleEn: p.quizTitleEn,
              format: p.quizFormat,
              theme: p.quizTheme,
              themeEn: p.quizThemeEn,
            };
            const inviteThemeLine = getQuizDisplayTheme(invQuizCard, i18n.language);
            return (
            <>
              <p className="text-muted small mb-2">
                {p.name} · {p.roomCode}
              </p>
              <div className="mb-2">
                <strong>{t('pvp.quizLabel', 'Quiz:')}</strong>{' '}
                <span className="d-inline-block">
                  {getQuizDisplayTitle(invQuizCard, i18n.language)}
                </span>
                {inviteThemeLine ? (
                  <div className="text-muted small mt-1 mb-0">
                    {inviteThemeLine}
                  </div>
                ) : null}
              </div>
              <div className="mb-2">
                <strong>{t('quiz.difficulty', 'Difficulty')}:</strong>{' '}
                {formatQuizDifficulty(p.quizDifficulty)}
              </div>
              <div className="mb-2 d-flex align-items-center flex-wrap gap-1">
                <strong className="text-nowrap">{t('pvp.host', 'Host')}:</strong>
                <span
                  className={
                    p.hostIsVip === true
                      ? 'vip-subscriber-nickname'
                      : undefined
                  }
                >
                  {p.hostNickname || '—'}
                </span>
                {p.hostIsVip === true && (
                  <i className="bi bi-gem text-warning" title="VIP" aria-hidden />
                )}
              </div>
              <div className="mb-0">
                <strong>{t('pvp.inviteJoinCost', 'Cost')}:</strong>{' '}
                <span className="d-inline-flex align-items-center gap-1 flex-wrap">
                  <InlineHalfHeartIcon className="pvp-invite-join-cost-heart" />
                  <span>× 1</span>
                </span>
              </div>
            </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer className="justify-content-between flex-wrap gap-2">
          <Button variant="outline-secondary" onClick={closeInviteJoinModal}>
            {t('common.cancel', 'Cancel')}
          </Button>
          {!isUserLoggedIn ? (
            <Button
              variant="primary"
              onClick={() => setShowLoginModal(true)}
              disabled={inviteJoinModal.loading || !!inviteJoinModal.error || !inviteJoinModal.preview}
            >
              {t('pvp.inviteJoinLogin', 'Log in to join')}
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={
                joining ||
                inviteJoinModal.loading ||
                !!inviteJoinModal.error ||
                !inviteJoinModal.preview
              }
              onClick={() => {
                const preview = inviteJoinModal.preview;
                if (!preview) return;
                const pathSeg =
                  preview.roomCode?.trim() || pvpJoinCode?.trim() || preview.roomId;
                const joinSource = inviteJoinModal.source;
                if (activePvpRoom && activePvpRoom.id !== preview.roomId) {
                  toast.error(
                    t('pvp.alreadyInAnotherRoom', 'You are already in another PvP room.')
                  );
                  return;
                }
                void joinRoom(preview.roomId, pathSeg, () => {
                  if (joinSource === 'url') {
                    onPvpJoinConsumed?.();
                  }
                  setInviteJoinModal({
                    open: false,
                    loading: false,
                    error: null,
                    preview: null,
                    source: null,
                  });
                });
              }}
            >
              {joining ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t('pvp.joining', 'Joining...')}
                </>
              ) : (
                <>
                  <i className="bi bi-play-circle me-2" aria-hidden />
                  {t('pvp.inviteJoinEnter', 'Enter room')}
                </>
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Login Modal */}
      <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('auth.loginRequired', 'Login Required')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LoginPage onLoginSuccess={handleLoginSuccess} />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default PvPLobby; 